import { useState } from "react"
import Screen from "./components/Screen"
import { spacing } from "./styles"

import "./TranslationService.scss"
import Button from "./components/Button"
import Flex from "../../components/Flex"
import Icons from "../../components/Icons"
import datasource from "../../datasource"
import TranslationRequestModal from "../../components/TranslationRequestModal"

interface Highlight {
    title: string
    text: string
}

interface Step {
    title: string
    text: string
    email?: string
}

interface TranslationServiceDoc {
    title: string
    subTitle: string
    intro: string
    heroCtaLabel: string
    highlights: {
        title: string
        items: Highlight[]
    }
    flow: {
        title: string
        subTitle: string
        steps: Step[]
    }
    cta: {
        title: string
        subTitle: string
        label: string
        email: string
    }
}

const {
    title,
    subTitle,
    intro,
    heroCtaLabel,
    highlights,
    flow,
    cta
} = datasource.translationService as TranslationServiceDoc

export default () => {

    const [requestOpen, setRequestOpen] = useState<boolean>(false)

    return (
        <div className="ts">
            <Screen className="ts__title-screen">
                <Flex vertical align="center" gap={spacing("4xl")}>
                    <Flex align="center" justify="center" className="ts__icon-box">
                        <Icons.TranslationService width={74} height={74}></Icons.TranslationService>
                    </Flex>
                    <div className="ts__title">{title}</div>
                    <div className="ts__subTitle">{subTitle}</div>
                    <div className="ts__intro">{intro}</div>
                    <Button onClick={() => setRequestOpen(true)}>
                        <Flex gap={spacing("md")} reposive={false} align="center">
                            {heroCtaLabel}
                            <Icons.ArrowUp width={16} height={16}></Icons.ArrowUp>
                        </Flex>
                    </Button>
                </Flex>
            </Screen>

            <Screen className="ts__highlights-screen">
                <div className="ts__panel">
                    <Flex vertical gap={spacing("6xl")}>
                        <div className="ts__section-title">{highlights.title}</div>
                        <div className="ts__highlights">
                            {highlights.items.map(it => <HighlightCard key={it.title} highlight={it}></HighlightCard>)}
                        </div>
                    </Flex>
                </div>
            </Screen>

            <Screen className="ts__flow-screen">
                <Flex vertical gap={spacing("6xl")}>
                    <Flex vertical gap={10}>
                        <div className="ts__section-title">{flow.title}</div>
                        <div className="ts__section-subTitle">{flow.subTitle}</div>
                    </Flex>
                    {/* plain div, not Flex: Flex writes an inline display:flex that would beat display:grid */}
                    <div className="ts__flow__steps">
                        {flow.steps.map((step, idx) => <StepCard key={step.title} step={step} index={idx}></StepCard>)}
                    </div>
                </Flex>
            </Screen>

            <div className="ts__cta-box">
                <Flex className="ts__cta" justify="space-between">
                    <Flex vertical gap={10}>
                        <div className="ts__cta__title">{cta.title}</div>
                        <div className="ts__cta__subTitle">{cta.subTitle}</div>
                    </Flex>
                    <Flex vertical justify="flex-end">
                        <Button onClick={() => setRequestOpen(true)}>
                            <Flex gap={spacing("md")} reposive={false} align="center">
                                {cta.label}
                                <Icons.ArrowUp width={16} height={16}></Icons.ArrowUp>
                            </Flex>
                        </Button>
                    </Flex>
                </Flex>
            </div>

            <TranslationRequestModal open={requestOpen} onClose={() => setRequestOpen(false)}/>
        </div>
    )
}

const HighlightCard = (props: { highlight: Highlight }) => {
    return (
        <Flex vertical className="highlight-card" gap={spacing("md")}>
            <div className="highlight-card__title">{props.highlight.title}</div>
            <div className="highlight-card__text">{props.highlight.text}</div>
        </Flex>
    )
}

const StepCard = (props: { step: Step, index: number }) => {
    return (
        <div className="step-card">
            <div className="step-card__badge">{props.index + 1}</div>
            <div className="step-card__title">{props.step.title}</div>
            <div className="step-card__text">
                {props.step.text}
                {props.step.email
                    ? <> <a className="step-card__email" href={`mailto:${props.step.email}`}>{props.step.email}</a></>
                    : null}
            </div>
        </div>
    )
}
